import { useManager } from 'dreamstate';
import React, { memo, useEffect, useRef, useState } from 'react';

import { Chats } from '@vimeo/bokeh-icons';
import { Optional } from '@vimeo/core/types/generic';
import { useLiveGlobals } from '@vimeo/live-core/components/providers/globals';
import { ChatManager, IChatContext } from '@vimeo/live-core/store/firebase/interaction/chat';
import { EChatType, IChatMessage } from '@vimeo/live-core/types/interaction_tools';
import { IInteractionStoreInitialState } from '@vimeo/live-core/types/store';
import { getAbsoluteNow } from '@vimeo/live-core/utils/general/time';

import { IconNotification } from './IconNotification';

/**
 * Hook for detect changes in chats and set true flag for notification.
 * @param isActive represents that chat is active module
 * @param isManagementAccessed represents that qna used on moderator or viewer side
 */

interface IPanelChatIconProps {
  isActive: boolean;
  isGlobal?: boolean;
  isManagementAccessed: boolean;
  chatContext?: IChatContext;
}

export const PanelChatIcon = memo(function ({
  isActive,
  isGlobal = true,
  isManagementAccessed,
  chatContext: {
    isEnabled,
    isBackstageEnabled,
    enabledAt,
    hydratedAt,
    [EChatType.BACKSTAGE]: { messages: rawBackstageMessages },
    [EChatType.PUBLIC]: { messages: rawPublicMessages },
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useManager(ChatManager),
}: IPanelChatIconProps) {
  const {
    initialState: { renderAt },
  } = useLiveGlobals<IInteractionStoreInitialState>();

  const lastSeen = useRef<number>((isGlobal ? (renderAt as number) : getAbsoluteNow()) + 500); // +500 for rendering delay
  const [hasNotification, setHasNotification] = useState<boolean>(false);

  const lastMessage: Optional<IChatMessage> = Object.values(
    isManagementAccessed && isBackstageEnabled
      ? { ...rawBackstageMessages, ...rawPublicMessages }
      : rawPublicMessages,
  ).reduce((acc, it) => {
    if (acc) {
      return acc.createdAt > it.createdAt ? acc : it;
    } else {
      return it;
    }
  }, null as Optional<IChatMessage>);

  useEffect(() => {
    setHasNotification(() => {
      if (isActive) {
        lastSeen.current = getAbsoluteNow();
        return false;
      } else {
        return Boolean(
          isEnabled &&
            ((lastMessage?.createdAt || 0) > lastSeen.current ||
              (enabledAt && enabledAt > lastSeen.current)),
        );
      }
    });
  }, [isActive, lastMessage, isEnabled, enabledAt]);

  useEffect(() => {
    if (hydratedAt) {
      lastSeen.current = Math.max(hydratedAt + 100, lastSeen.current);
    }
  }, [hydratedAt]);

  return (
    <IconNotification isVisible={hasNotification}>
      <Chats />
    </IconNotification>
  );
});
